import { Storage, StorageOptions } from './storage';

/**
 * Jotai의 AsyncStorage 인터페이스
 * jotai/utils에서 export되지 않으므로 직접 정의
 */
interface AsyncStorage<T> {
  getItem(key: string, initialValue: T): Promise<T>;
  setItem(key: string, value: T): Promise<void>;
  removeItem(key: string): Promise<void>;
}

/**
 * Storage를 Jotai에서 사용할 수 있는 AsyncStorage로 wrapping하는 클래스
 * atomWithStorage에서 직접 사용 가능하며, 제네릭을 지원합니다.
 */
export class JotaiStorageWrapper<T = unknown> implements AsyncStorage<T> {
  constructor(
    private storage: Storage,
    private storageOptions?: StorageOptions
  ) {}

  async getItem(key: string, initialValue: T): Promise<T> {
    const value = this.storage.get<T>(key, this.storageOptions);
    // Return initialValue if storage fails (e.g., in incognito mode)
    return value ?? initialValue;
  }

  async setItem(key: string, value: T): Promise<void> {
    this.storage.set(key, value, this.storageOptions);
  }

  async removeItem(key: string): Promise<void> {
    this.storage.remove(key);
  }

  clear(): void {
    this.storage.clear();
  }
}

/**
 * JotaiStorageWrapper를 생성하는 팩토리 함수
 * @param storage - 사용할 Storage 인스턴스
 * @param storageOptions - Storage 옵션 (expiry 등)
 * @returns 제네릭을 지원하는 AsyncStorage 인스턴스
 *
 * @example
 * ```typescript
 * import { storage } from '@/shared/lib';
 * import { atomWithStorage } from 'jotai/utils';
 *
 * const jotaiStorage = createJotaiStorage(storage);
 * const userAtom = atomWithStorage<User>('user', defaultUser, jotaiStorage);
 * ```
 */
export function createJotaiStorage<T = unknown>(
  storage: Storage,
  storageOptions?: StorageOptions
): AsyncStorage<T> {
  return new JotaiStorageWrapper<T>(storage, storageOptions);
}

/**
 * 창별로 격리된 Storage를 생성하는 팩토리 함수
 * sessionStorage를 사용하여 각 브라우저 창마다 독립적인 상태를 유지합니다.
 *
 * @param prefix - Storage key prefix (선택적)
 * @param storageOptions - Storage 옵션 (expiry 등)
 * @returns 창별로 격리된 AsyncStorage 인스턴스
 *
 * @example
 * ```typescript
 * import { atomWithStorage } from 'jotai/utils';
 * import { createWindowIsolatedStorage } from '@/shared/lib';
 *
 * // 각 브라우저 창마다 독립적인 탭 상태 유지
 * const tabsAtom = atomWithStorage<TabState>(
 *   'tabs',
 *   defaultTabState,
 *   createWindowIsolatedStorage('app')
 * );
 * ```
 */
export function createWindowIsolatedStorage<T = unknown>(
  prefix: string = '',
  storageOptions?: StorageOptions
): AsyncStorage<T> {
  const storage = new Storage(prefix, {
    windowIsolated: true,
    storageType: 'session',
  });
  return new JotaiStorageWrapper<T>(storage, storageOptions);
}
